import { useTranslate } from '@ur-solutions/react-hooks'
import { useCallback } from 'react'

// The backend seeds `InfringementCategory.description` in Norwegian and can
// only render Norwegian or English itself. Category identifiers are stable, so the UI
// translates the same texts into every locale by identifier instead of showing
// whatever wording the API happens to return.

/** Locale key per distinct category wording. */
const CATEGORY_DESCRIPTION_MESSAGES = {
  'conductor-minimum-age':
    'infringements.category-description.conductor-minimum-age',
  'daily-driving-9h': 'infringements.category-description.daily-driving-9h',
  'daily-driving-13-5h-no-break':
    'infringements.category-description.daily-driving-13-5h-no-break',
  'daily-driving-10h': 'infringements.category-description.daily-driving-10h',
  'daily-driving-15h-no-break':
    'infringements.category-description.daily-driving-15h-no-break',
  'weekly-driving-56h': 'infringements.category-description.weekly-driving-56h',
  'fortnightly-driving-90h':
    'infringements.category-description.fortnightly-driving-90h',
  'break-45min': 'infringements.category-description.break-45min',
  'daily-rest-11h': 'infringements.category-description.daily-rest-11h',
  'daily-rest-9h': 'infringements.category-description.daily-rest-9h',
  'daily-rest-split': 'infringements.category-description.daily-rest-split',
  'daily-rest-multi-manning':
    'infringements.category-description.daily-rest-multi-manning',
  'weekly-rest-24h': 'infringements.category-description.weekly-rest-24h',
  'weekly-rest-45h': 'infringements.category-description.weekly-rest-45h',
  'weekly-rest-overdue':
    'infringements.category-description.weekly-rest-overdue',
  'twelve-24h-periods': 'infringements.category-description.twelve-24h-periods',
  'weekly-rest-after-12-periods':
    'infringements.category-description.weekly-rest-after-12-periods',
  'night-driving-3h': 'infringements.category-description.night-driving-3h',
  'pay-linked-to-distance':
    'infringements.category-description.pay-linked-to-distance',
  'work-organisation': 'infringements.category-description.work-organisation',
  'tachograph-not-installed':
    'infringements.category-description.tachograph-not-installed',
  'tachograph-not-inspected':
    'infringements.category-description.tachograph-not-inspected',
  'multiple-driver-cards':
    'infringements.category-description.multiple-driver-cards',
  'falsified-driver-card':
    'infringements.category-description.falsified-driver-card',
  'driver-card-not-holder':
    'infringements.category-description.driver-card-not-holder',
  'driver-card-obtained-falsely':
    'infringements.category-description.driver-card-obtained-falsely',
  'tachograph-malfunction':
    'infringements.category-description.tachograph-malfunction',
  'tachograph-misuse': 'infringements.category-description.tachograph-misuse',
  'fraudulent-device': 'infringements.category-description.fraudulent-device',
  'data-falsified': 'infringements.category-description.data-falsified',
  'records-not-retained':
    'infringements.category-description.records-not-retained',
  'records-not-available-one-year':
    'infringements.category-description.records-not-available-one-year',
  'records-incorrect-use':
    'infringements.category-description.records-incorrect-use',
  'records-unauthorised-removal':
    'infringements.category-description.records-unauthorised-removal',
  'records-used-too-long':
    'infringements.category-description.records-used-too-long',
  'records-unreadable': 'infringements.category-description.records-unreadable',
  'manual-entries-missing':
    'infringements.category-description.manual-entries-missing',
  'wrong-record-or-slot':
    'infringements.category-description.wrong-record-or-slot',
  'mode-switch-misuse': 'infringements.category-description.mode-switch-misuse',
  'refuses-check': 'infringements.category-description.refuses-check',
  'cannot-produce-records-28-days':
    'infringements.category-description.cannot-produce-records-28-days',
  'cannot-produce-card-records':
    'infringements.category-description.cannot-produce-card-records',
  'cannot-produce-manual-records':
    'infringements.category-description.cannot-produce-manual-records',
  'cannot-produce-driver-card':
    'infringements.category-description.cannot-produce-driver-card',
  'tachograph-not-repaired':
    'infringements.category-description.tachograph-not-repaired',
  'manual-records-missing-when-broken':
    'infringements.category-description.manual-records-missing-when-broken',
} as const

type CategoryDescriptionGroup = keyof typeof CATEGORY_DESCRIPTION_MESSAGES

/** Which categories share each wording, mirroring the seeded descriptions. */
const GROUP_IDENTIFIERS: Record<CategoryDescriptionGroup, readonly string[]> = {
  'conductor-minimum-age': ['A1'],
  'daily-driving-9h': ['B1', 'B2', 'B3'],
  'daily-driving-13-5h-no-break': ['B4'],
  'daily-driving-10h': ['B5', 'B6', 'B7'],
  'daily-driving-15h-no-break': ['B8'],
  'weekly-driving-56h': ['B9', 'B10', 'B11', 'B12'],
  'fortnightly-driving-90h': ['B13', 'B14', 'B15', 'B16'],
  'break-45min': ['C1', 'C2', 'C3'],
  'daily-rest-11h': ['D1', 'D2', 'D3'],
  'daily-rest-9h': ['D4', 'D5', 'D6'],
  'daily-rest-split': ['D7', 'D8', 'D9'],
  'daily-rest-multi-manning': ['D10', 'D11', 'D12'],
  'weekly-rest-24h': ['D13', 'D14', 'D15'],
  'weekly-rest-45h': ['D16', 'D17', 'D18'],
  'weekly-rest-overdue': ['D19', 'D20', 'D21'],
  'twelve-24h-periods': ['E1', 'E2', 'E3'],
  'weekly-rest-after-12-periods': ['E4', 'E5'],
  'night-driving-3h': ['E6', 'E7'],
  'pay-linked-to-distance': ['F1'],
  'work-organisation': ['F2'],
  'tachograph-not-installed': ['G1'],
  'tachograph-not-inspected': ['H1'],
  'multiple-driver-cards': ['H2'],
  'falsified-driver-card': ['H3'],
  'driver-card-not-holder': ['H4'],
  'driver-card-obtained-falsely': ['H5'],
  'tachograph-malfunction': ['H6'],
  'tachograph-misuse': ['H7'],
  'fraudulent-device': ['H8'],
  'data-falsified': ['H9'],
  'records-not-retained': ['H10'],
  'records-not-available-one-year': ['H11'],
  'records-incorrect-use': ['H12'],
  'records-unauthorised-removal': ['H13'],
  'records-used-too-long': ['H14'],
  'records-unreadable': ['H15'],
  'manual-entries-missing': ['H16'],
  'wrong-record-or-slot': ['H17'],
  'mode-switch-misuse': ['H18'],
  'refuses-check': ['I1'],
  'cannot-produce-records-28-days': ['I2'],
  'cannot-produce-card-records': ['I3'],
  'cannot-produce-manual-records': ['I4'],
  'cannot-produce-driver-card': ['I5'],
  'tachograph-not-repaired': ['J1'],
  'manual-records-missing-when-broken': ['J2'],
}

const GROUP_BY_IDENTIFIER = new Map<string, CategoryDescriptionGroup>(
  Object.entries(GROUP_IDENTIFIERS).flatMap(([group, identifiers]) =>
    identifiers.map(
      identifier => [identifier, group as CategoryDescriptionGroup] as const
    )
  )
)

/** The group whose wording a category identifier uses, if this table knows it. */
export function infringementCategoryDescriptionGroup(
  identifier: string | null | undefined
) {
  return identifier
    ? GROUP_BY_IDENTIFIER.get(identifier.toUpperCase())
    : undefined
}

/** The locale key holding a category's description, if this table knows it. */
export function infringementCategoryDescriptionKey(
  identifier: string | null | undefined
) {
  const group = infringementCategoryDescriptionGroup(identifier)

  return group && CATEGORY_DESCRIPTION_MESSAGES[group]
}

/**
 * Resolve an infringement category description in the viewer's language.
 *
 * Unknown identifiers fall back to the description the API returned, so a newly
 * seeded category still shows its text until it is translated here.
 */
export function useInfringementCategoryDescription() {
  const descriptions = useTranslate(CATEGORY_DESCRIPTION_MESSAGES)

  return useCallback(
    (identifier: string | null | undefined, apiDescription?: string | null) => {
      const group = infringementCategoryDescriptionGroup(identifier)

      return (group && descriptions[group]) || apiDescription || ''
    },
    [descriptions]
  )
}
